import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type {
	CascadingSelectValue,
	FieldInfo,
	FormAnswers,
} from '@atlassian/conversation-assistant-message-actions-api/jsm';
import type {
	ActionRendererKeyToPayload,
	ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';

// Helper function to filter out summary, description, and empty values
export const filterFieldValues = (
	requestFieldValues: Record<string, unknown>,
): [string, unknown][] =>
	Object.entries(requestFieldValues).filter(
		([key, value]) =>
			key !== 'summary' &&
			key !== 'description' &&
			value !== undefined &&
			value !== null &&
			value !== '',
	);

// Helper function to format cascading select values
const formatCascadingSelectValue = (value: CascadingSelectValue): string => {
	let result = value.value;
	if (value.child) {
		result += ` > ${formatCascadingSelectValue(value.child)}`;
	}
	return result;
};

// Helper function to check if an array is an asset/CMDB field (array of { objectId, workspaceId, id } objects)
const isAssetField = (value: unknown[]): boolean => {
	return (
		value.length > 0 &&
		value.every(
			(item) =>
				item &&
				typeof item === 'object' &&
				'objectId' in item &&
				'workspaceId' in item &&
				'id' in item,
		)
	);
};

// Helper function to check if an array is a multiselect field (array of { value: string } objects)
const isMultiselectField = (value: unknown[]): boolean => {
	return (
		value.length > 0 &&
		value.every(
			(item) =>
				item &&
				typeof item === 'object' &&
				'value' in item &&
				typeof (item as { value: unknown }).value === 'string',
		)
	);
};

// Helper function to check if an array is a components field (array of { name: string } objects)
const isComponentsField = (value: unknown[]): boolean => {
	return (
		value.length > 0 &&
		value.every(
			(item) =>
				item &&
				typeof item === 'object' &&
				'name' in item &&
				!('id' in item) &&
				typeof (item as { name: unknown }).name === 'string',
		)
	);
};

// Helper function to check if an object is a cascading select field
const isCascadingSelectField = (value: unknown): value is CascadingSelectValue => {
	return Boolean(
		value &&
		typeof value === 'object' &&
		'value' in value &&
		typeof (value as { value: unknown }).value === 'string' &&
		(!('child' in value) ||
			(typeof (value as { child?: unknown }).child === 'object' &&
				(value as { child?: unknown }).child !== null)),
	);
};

export const createFieldInfoFromResponse = (
	requestFieldValues: Record<string, unknown> = {},
	requestFieldNames: Record<string, unknown> = {},
	formFieldNames: Record<string, unknown> = {},
	formFieldChoices: unknown = {},
	assetNames: Record<string, string> = {},
): FieldInfo[] => {
	const choiceMaps =
		formFieldChoices && typeof formFieldChoices === 'object'
			? (formFieldChoices as Record<string, Record<string, string>>)
			: {};

	return filterFieldValues(requestFieldValues).map(([fieldId, value]) => {
		// Handle different field types
		let displayValue: string = '';
		let actualValue: unknown;
		let fieldType:
			| 'user'
			| 'priority'
			| 'doc'
			| 'multiselect'
			| 'cascading'
			| 'components'
			| 'asset'
			| 'unknown' = 'unknown';

		if (value && typeof value === 'object' && !Array.isArray(value)) {
			// Handle object values
			if (isCascadingSelectField(value)) {
				// Handle cascading select field
				actualValue = value;
				fieldType = 'cascading';
				displayValue = formatCascadingSelectValue(value);
			} else if ('value' in value) {
				// Handle nested value object like { value: "High" }
				actualValue = (value as { value: unknown }).value;
				displayValue = String(actualValue);
			} else if ('name' in value && 'id' in value) {
				// Handle user field like { name: "John Doe", id: "123" }
				actualValue = (value as { name: string; id: string }).name;
				fieldType = 'user';
				displayValue = String(actualValue);
			} else if ('name' in value && !('id' in value)) {
				// Handle priority field like { name: "High" }
				actualValue = (value as { name: string }).name;
				fieldType = 'priority';
				displayValue = String(actualValue);
			} else if ('type' in value && 'content' in value) {
				// Handle ADF document field like { type: "doc", content: [...] }
				actualValue = value;
				fieldType = 'doc';
				displayValue = '[Rich Text Content]'; // Placeholder for ADF content
			} else {
				// Handle other object types
				actualValue = value;
				displayValue = String(actualValue);
			}
		} else if (Array.isArray(value)) {
			// Handle array values
			actualValue = value;
			// Check if it's an asset/CMDB field (array of { objectId, workspaceId, id } objects)
			if (isAssetField(value)) {
				fieldType = 'asset';
				displayValue = value
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					.map((item: any) => assetNames[item.objectId] ?? item.objectId)
					.join(', ');
			} else if (isMultiselectField(value)) {
				// Check if it's a multiselect field (array of { value: string } objects)
				fieldType = 'multiselect';
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				displayValue = value.map((item: any) => item.value).join(', ');
			} else if (isComponentsField(value)) {
				// Check if it's a components field (array of { name: string } objects)
				fieldType = 'components';
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				displayValue = value.map((item: any) => item.name).join(', ');
			} else if (
				value.length > 0 &&
				value[0] &&
				typeof value[0] === 'object' &&
				'name' in value[0] &&
				'id' in value[0]
			) {
				// Check if it's an array of user objects
				fieldType = 'user';
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				displayValue = value.map((user: any) => user.name).join(', ');
			} else {
				// Handle other array types (e.g. form choice IDs or asset choices)
				const choiceMap = choiceMaps[fieldId];
				if (choiceMap && value.every((item) => typeof item === 'string')) {
					displayValue = value.map((item) => choiceMap[item as string] || String(item)).join(', ');
				} else if (
					Object.keys(assetNames).length > 0 &&
					value.every(
						(item) =>
							typeof item === 'string' &&
							/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:.+$/i.test(
								item as string,
							),
					)
				) {
					// Handle asset choices in form answers (format: "workspaceId:objectId")
					displayValue = value
						.map((item) => {
							const objectId = (item as string).split(':').slice(1).join(':');
							return assetNames[objectId] ?? item;
						})
						.join(', ');
				} else {
					displayValue = value.join(', ');
				}
			}
		} else {
			// Handle direct values
			actualValue = value;
			const choiceMap = choiceMaps[fieldId];
			if (choiceMap && typeof actualValue === 'string') {
				displayValue = choiceMap[actualValue] || actualValue;
			} else {
				displayValue = String(actualValue);
			}
		}

		return {
			id: fieldId,
			label: String(requestFieldNames[fieldId] || formFieldNames[fieldId] || fieldId),
			type: fieldType,
			value: actualValue,
			displayValue,
		};
	});
};

// Helper function to convert form.answers to field values format
export const convertFormAnswersToFieldValues = (
	formAnswers: FormAnswers = {},
): Record<string, unknown> => {
	const fieldValues: Record<string, unknown> = {};

	Object.entries(formAnswers).forEach(([questionId, answer]) => {
		if (answer.text !== undefined) {
			fieldValues[questionId] = answer.text;
		} else if (answer.user !== undefined) {
			fieldValues[questionId] = answer.user;
		} else if (Array.isArray(answer.users)) {
			fieldValues[questionId] = answer.users;
		} else if (Array.isArray(answer.choices)) {
			fieldValues[questionId] = answer.choices;
		} else if (
			typeof answer.date === 'string' &&
			typeof answer.time === 'string' &&
			answer.date &&
			answer.time
		) {
			fieldValues[questionId] = `${answer.date} ${answer.time}`;
		} else if (answer.date !== undefined) {
			fieldValues[questionId] = answer.date;
		} else if (answer.time !== undefined) {
			fieldValues[questionId] = answer.time;
		} else {
			// Preserve any primitive fallback values to avoid silently hiding fields.
			const fallbackValue = Object.values(answer).find(
				(value) =>
					typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean',
			);
			if (fallbackValue !== undefined) {
				fieldValues[questionId] = fallbackValue;
			}
		}
	});

	return fieldValues;
};

export const isActionWithInputs = (
	action: ActionWith<
		| ActionRendererKeyToPayload[typeof ActionKeys.JsmCreateRequest]
		| ActionRendererKeyToPayload[typeof ActionKeys.JsmCreateRequestV2]
		| ActionRendererKeyToPayload[typeof ActionKeys.CreateRequest]
	>,
): action is ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JsmCreateRequestV2]> => {
	return 'inputs' in action.data ? true : false;
};
